/**
 * Übertragungszustand eines Objekts für den Kopf von Verfügbarkeit & Vertrieb – nur was das
 * Lesemodell belegt: die jüngste ARI-Operation, ihr Zeitpunkt und wie viele Apartments mit einer
 * fehlgeschlagenen letzten Übertragung warten. Es gibt keinen Verbindungsstatus zu Channex.
 */
import type { AvailabilityDistributionApartment } from '@/shared/api';

type Status = AvailabilityDistributionApartment['operations'][number]['status'];

export type TransmissionTone = 'confirmed' | 'sent' | 'running' | 'failed' | 'none';

export interface TransmissionSummary {
  label: string;
  tone: TransmissionTone;
  /** Uhrzeit (heute) oder Datum der jüngsten Operation. */
  lastAt: string | null;
  failedApartments: number;
  /** Apartments, deren letzte Änderung nicht gesendet wurde (`Suppressed`). */
  unsentApartments: number;
  /** Nachladen gescheitert: der Satz beschreibt den zuletzt geladenen Stand. */
  stale?: boolean;
}

const OPEN: ReadonlySet<Status> = new Set(['Queued', 'Retrying']);

function describe(status: Status | undefined): { label: string; tone: TransmissionTone } {
  if (!status) return { label: 'Noch nichts übertragen', tone: 'none' };
  if (status === 'Confirmed') return { label: 'Von Channex bestätigt', tone: 'confirmed' };
  if (status === 'SentUnverified') return { label: 'An Channex gesendet', tone: 'sent' };
  if (status === 'Failed') return { label: 'Übertragung fehlgeschlagen', tone: 'failed' };
  if (status === 'Suppressed') return { label: 'Nicht gesendet', tone: 'failed' };
  return { label: 'Übertragung läuft', tone: 'running' };
}

function when(iso: string, now: Date): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const sameDay = date.toDateString() === now.toDateString();
  return new Intl.DateTimeFormat(
    'de-DE',
    sameDay
      ? { hour: '2-digit', minute: '2-digit' }
      : { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' },
  ).format(date);
}

export function summarizeTransmissions(
  apartments: readonly AvailabilityDistributionApartment[],
  now = new Date(),
): TransmissionSummary {
  const all = apartments.flatMap((apartment) => apartment.operations);
  const latest = [...all].sort((left, right) => right.createdAt.localeCompare(left.createdAt))[0];
  // Was noch unterwegs ist, geht vor: ein jüngeres „bestätigt“ hieße sonst, alles sei durch.
  const open = all.find((operation) => OPEN.has(operation.status));
  const latestOf = (status: Status) =>
    apartments.filter((apartment) => apartment.operations[0]?.status === status).length;
  const failedApartments = latestOf('Failed');
  const unsentApartments = latestOf('Suppressed');
  const described = describe(open?.status ?? latest?.status);
  return {
    ...described,
    // Ein jüngeres „bestätigt“ darf ein gescheitertes Apartment nicht grün übermalen.
    tone: !open && (failedApartments > 0 || unsentApartments > 0) ? 'failed' : described.tone,
    lastAt: latest ? when(latest.createdAt, now) : null,
    failedApartments,
    unsentApartments,
  };
}

/** „Von Channex bestätigt · letzte Übertragung 20:31 · 1 fehlgeschlagen“ */
export function transmissionSentence(summary: TransmissionSummary): string {
  const parts = [summary.label];
  if (summary.lastAt) parts.push(`letzte Übertragung ${summary.lastAt}`);
  if (summary.failedApartments > 0) parts.push(`${summary.failedApartments} fehlgeschlagen`);
  if (summary.unsentApartments > 0) parts.push(`${summary.unsentApartments} nicht gesendet`);
  if (summary.stale) parts.push('Nachladen fehlgeschlagen');
  return parts.join(' · ');
}

/**
 * Nachfragetakt der Fläche: alle 2 s, solange eine Übertragung offen ist, sonst gar nicht. Scheitert
 * das Nachladen, hört die Fläche auf – „Erneut laden“ holt den Stand bewusst.
 */
export function transmissionPollInterval(
  apartments: readonly AvailabilityDistributionApartment[] | undefined,
  failed = false,
): number | false {
  return !failed &&
    apartments?.some((apartment) =>
      apartment.operations.some((operation) => OPEN.has(operation.status)),
    )
    ? 2_000
    : false;
}
